const week1 = {
  number: 1,
  title: "Foundations",
  theme: "Why no map exists—and why that's not a problem",
  modules: [
    {
      id: "1.1",
      title: "The World Without a Map",
      content: `You were born into a world without a map.

Not because someone forgot to give you one, not because you failed to earn one, not because you misplaced it along the way—but because no such map exists.

This is not a failure. It is not a limitation to be overcome. It is the fundamental structure of human experience.

Humans move through life the way sailors once crossed dark oceans: by sensing more than by seeing, by feeling shifts before understanding them, by navigating currents they cannot predict, discovering their direction only after they begin to move.

Most people believe something is wrong with them because life never feels fully visible. They experience doubt, conflict, inconsistency, longing, fear, collapse—and interpret these as personal failures. They assume that somewhere, somehow, other people have figured out the path. That clarity exists and they simply lack access to it.

But the truth is simpler and far kinder: You were never meant to see the whole path. You were meant to navigate it.

This distinction—between seeing and navigating—is the foundation of everything that follows.

Seeing implies the path already exists, laid out ahead of you, waiting to be discovered. Navigation implies the path is created through movement itself. You do not find your way; you make it.

This course begins where certainty ends.`
    },
    {
      id: "1.2",
      title: "The Null Hypothesis",
      content: `The framework you are about to learn rests on a single foundational recognition:

**What I don't know will always exceed what I know.**

Read that again. Let it settle.

This is not pessimism. It is not intellectual humility performed for effect. It is not a limitation to be overcome with enough effort, education, or insight.

It is the structure of reality itself.

Uncertainty is infinite; certainty is always finite. No matter how much you learn, the unknown will always dwarf the known. Your consciousness reaches for certainty, yet reality itself is built from uncertainty.

This recognition is called the **Null Hypothesis**—not as a statistical term, but as a starting position. It is what remains when all assumptions are stripped away.

From this tension—a consciousness that craves certainty living in a reality built from uncertainty—a simple conclusion emerges:

**The human system is designed for uncertainty.**

You are not broken because you feel uncertain. You are functioning exactly as designed. Your task is not to eliminate the unknown but to move within it—from not knowing toward knowing, from uncertainty toward the small islands of certainty you earn through navigation.

Certainty is not the starting point. It is the byproduct of navigating uncertainty well.

This changes everything. If what I don't know will always exceed what I know, then reality itself is the unknown domain. What I "know" serves merely as a functional platform for navigation, not as truth about reality. Effective decision-making must therefore work with uncertainty rather than seeking to eliminate it.`
    },
    {
      id: "1.3",
      title: "Certainty as Byproduct",
      content: `Consider how you learned to walk.

You did not study the physics of bipedal locomotion. You did not develop a comprehensive plan for transferring weight from one foot to another. You did not wait until you understood balance before attempting movement.

You moved. You fell. You adjusted. You moved again.

Through this process—this navigation through uncertainty—something emerged: the ability to walk. Not as a concept you understood, but as a capacity you developed. Certainty (you can walk) emerged as a byproduct of navigating uncertainty (you didn't know how).

This is the pattern at every level of human development.

You did not understand language before you spoke. You did not understand relationships before you loved. You did not understand yourself before you began becoming yourself.

In each case, certainty emerged from navigation—not the reverse.

Yet somewhere along the way, you were taught to expect the opposite. You were taught that understanding should precede action. That clarity should precede movement. That you should know before you do.

This teaching is backwards.

**Clarity emerges from movement, not before it.** The map is drawn as you walk.

This is not an argument for recklessness or thoughtlessness. It is an argument for understanding the actual sequence: you move, you sense the terrain, you adjust, you learn, you move again. Each cycle deposits a small island of certainty—not certainty about the whole path, but certainty about what you just learned.

Over time, these islands connect. A geography emerges. Not the geography that existed before you started, but the geography you created through navigation.

This is how minds actually work. This is how lives actually unfold.`
    },
    {
      id: "1.4",
      title: "Navigation vs. Optimization",
      content: `There is a seductive alternative to navigation. It is called optimization.

Optimization assumes the goal is known. It then works backward to find the most efficient path to that goal. It measures, calculates, minimizes friction, maximizes output.

Optimization works beautifully—when the goal is known. When you know exactly what you want and exactly how to measure progress toward it, optimization is the right tool.

But here is the problem: **the most important questions of human life resist optimization.**

Who should I become? What kind of relationships do I want? What work is meaningful to me? How should I navigate this conflict? What matters?

These questions do not have known answers to work backward from. They cannot be measured. They cannot be optimized. Attempting to optimize them leads to strange distortions—you find yourself pursuing goals you thought you wanted, achieving metrics that were supposed to mean something, arriving at destinations that feel empty.

Navigation is different.

Navigation does not assume the goal is known. Navigation assumes the direction can be sensed—even when the destination cannot be seen.

**You are not a problem to be solved. You are a system designed to move through uncertainty.**

This reframe is crucial. Optimization treats you as a problem: something suboptimal that needs fixing, something inefficient that needs improvement, something broken that needs repair.

Navigation treats you as a vessel: something capable of movement, something equipped for uncertainty, something designed to find its way through conditions it cannot predict.

The difference is not merely philosophical. It changes what you pay attention to.

Optimization asks: What is wrong? What needs fixing? What am I missing?

Navigation asks: What direction am I sensing? What is the next true step? What did I learn from the last one?

This course teaches navigation. Not because optimization is always wrong, but because for the deepest questions of human life, navigation is the only approach that works.`
    },
    {
      id: "1.5",
      title: "The Invitation",
      content: `Over the next eight weeks, you will learn a framework for navigating uncertainty.

This framework has three integrated components:

**The Inner House** — the architecture of your mind. You will learn that you are not a single self but a dwelling inhabited by multiple residents, each with their own fears, preferences, and ways of interpreting the world. You will learn how these residents coordinate, conflict, and shift leadership depending on circumstances.

**Directional Identity** — the tools that allow navigation. You will learn about direction (the pull toward "there" rather than "here"), articulation (how you tell yourself the truth), anchors (what holds you stable), and rules (the commitments that maintain coherence).

**The Navigational Way** — the process of movement itself. You will learn about the Next True Step, the Containment Window, the Review Moment, and the Navigation Loop that turns experience into learning.

You will also learn about rhythm—the cyclical nature of human capacity, meaning, identity, and connection. About friction—the productive and unproductive tensions between parts of yourself. About collapse—what it is, why it happens, and how to rebuild.

What this framework offers:

- Vocabulary for what you already experience but may not have words for
- Architecture to understand the structure of your mind
- Process for moving through uncertainty with intention

What this framework does not offer:

- The map (it doesn't exist)
- The destination (only you can sense that)
- Certainty (only navigation produces this, one step at a time)

**The Navigational Mind is not a fixed structure to be achieved. It is a capacity to be developed.** It is the ongoing ability to navigate uncertainty with direction, articulation, and coherence.

This course is a containment window—eight weeks within which you will encounter these ideas and begin applying them to your own experience. The learning happens not through understanding alone, but through navigation itself.

You are ready to begin.`
    }
  ],
  reflection: {
    prompt: "Consider a current uncertainty in your life. Not to solve it—just to name it. What does it feel like to acknowledge that no map exists for this?",
    placeholder: "Write your reflection here. This is for you—a space to apply the week's concepts to your own experience. Your reflections are saved locally and can be downloaded anytime."
  }
};

export default week1;
